import { useForm } from "@inertiajs/react";
import {
  Children,
  cloneElement,
  type FormEvent,
  type InputHTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useState,
} from "react";

import backgroundGreen from "@/assets/background_green.svg";
import AuthCard from "@/components/AuthCard";
import Button from "@/components/Button";
import LinkButton from "@/components/LinkButton";
import SearchableSelect, {
  type SelectOption,
} from "@/components/SearchableSelect";
import { profileUser, users } from "@/routes";
import type { User } from "@/types";

// "Cursos para:" — only shown for Los Tilos users; La Cuesta has a single
// fixed category with nothing to pick.
const LOS_TILOS_CATEGORY_OPTIONS: SelectOption[] = [
  { value: "senora", label: "Señoras" },
  { value: "universitaria", label: "Universitarias" },
  { value: "numeraria_auxiliar", label: "Numerarias Auxiliares" },
  { value: "agregada", label: "Agregadas" },
  { value: "numeraria", label: "Numerarias" },
];

const FIELD_INPUT_CLASSES =
  "w-full border-0 bg-transparent p-0 font-sans text-base font-light text-ink shadow-none placeholder:text-ink/40 focus:outline-none focus:ring-0 focus:shadow-none";

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="mt-2 px-1 font-sans text-sm font-light text-ink/70 italic first:mt-0">
      {children}
    </p>
  );
}

/** Labels a single input/select the same way the admin edit form does: the
 * name sits on the border as a legend once the field has a value, so a
 * filled field stays identifiable. While empty, only the placeholder (also
 * derived from `label`) shows, so the two don't display redundantly.
 * `forceLabel` keeps the legend on even when empty — native date inputs
 * always render their own "dd-mm-aaaa" placeholder, so the field never
 * reads as empty and the label must stay put to identify it. */
function Field({
  label,
  forceLabel = false,
  children,
}: {
  label: string;
  forceLabel?: boolean;
  children: ReactNode;
}) {
  const child = Children.only(children);
  const value = isValidElement(child)
    ? (child.props as { value?: unknown }).value
    : undefined;
  const hasValue =
    forceLabel || (value !== undefined && value !== null && value !== "");

  const content = isValidElement(child)
    ? cloneElement(child as ReactElement<{ placeholder?: string }>, {
        placeholder:
          (child.props as { placeholder?: string }).placeholder ?? label,
      })
    : child;

  return (
    <fieldset
      className={`min-w-0 rounded-full border-2 border-ink px-4 focus-within:border-steel ${hasValue ? "pb-2.5" : "py-3.5"}`}
    >
      {hasValue && (
        <legend className="truncate px-1.5 font-sans text-xs text-ink/60">
          {label}
        </legend>
      )}
      {content}
    </fieldset>
  );
}

function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={FIELD_INPUT_CLASSES} {...props} />;
}

function PasswordInput(
  props: Omit<InputHTMLAttributes<HTMLInputElement>, "type">,
) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="flex items-center gap-2">
      <Input type={visible ? "text" : "password"} {...props} />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="flex shrink-0 items-center text-ink/60 transition-colors hover:text-ink"
        aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
        aria-pressed={visible}
        tabIndex={-1}
      >
        {visible ? <EyeIcon /> : <EyeOffIcon />}
      </button>
    </div>
  );
}

type Props = {
  data: User;
};

export default function UsersEditProfile({ data: user }: Props) {
  const needsInfoUpdate = user.needs_info_update as boolean;
  // La Cuesta has a single fixed category with nothing to pick, so it's
  // forced here rather than left to whatever the field happened to hold.
  const isLosTilos = user.origin === "los_tilos";

  const form = useForm({
    email: user.email ?? "",
    phone_number: user.phone_number ?? "",
    first_name: user.first_name ?? "",
    last_name: user.last_name ?? "",
    second_last_name: user.second_last_name ?? "",
    birth_date: user.birth_date ?? "",
    user_category: isLosTilos ? (user.user_category ?? "") : "la_cuesta",
    address: user.address ?? "",
    commune: user.commune ?? "",
    city: user.city ?? "",
    password: "",
    password_confirmation: "",
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    form.transform((data) => ({ user: data }));
    form.patch(users.update(user.id).url);
  }

  return (
    <AuthCard
      title={`¡Hola ${user.first_name}!`}
      subtitle="Revisa y configura tus datos aquí"
      background={backgroundGreen}
    >
      <form onSubmit={submit} className="mx-auto flex max-w-xl flex-col gap-4">
        {needsInfoUpdate && (
          <div className="rounded-2xl border-2 border-deep-red/40 bg-deep-red/5 px-4 py-3 font-sans text-sm text-ink">
            <p className="font-semibold">Actualiza tus datos</p>
            <p className="mt-1 font-light">
              Te invitamos a revisar y actualizar tus datos personales para
              mejorar tu experiencia en las actividades.
            </p>
          </div>
        )}

        <SectionLabel>Datos de contacto</SectionLabel>

        <Field label="Correo electrónico">
          <Input
            type="email"
            autoComplete="email"
            value={form.data.email}
            onChange={(e) => form.setData("email", e.target.value)}
            required
          />
        </Field>

        <Field label="Número de teléfono (ejemplo: +56912345678)">
          <Input
            type="tel"
            placeholder="+56912345678"
            autoComplete="tel"
            value={form.data.phone_number}
            onChange={(e) => form.setData("phone_number", e.target.value)}
            required
          />
        </Field>

        <SectionLabel>Datos personales</SectionLabel>

        <Field label="Nombre">
          <Input
            autoComplete="given-name"
            value={form.data.first_name}
            onChange={(e) => form.setData("first_name", e.target.value)}
            required
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Apellido paterno">
            <Input
              autoComplete="family-name"
              value={form.data.last_name}
              onChange={(e) => form.setData("last_name", e.target.value)}
              required
            />
          </Field>
          <Field label="Apellido materno">
            <Input
              value={form.data.second_last_name}
              onChange={(e) => form.setData("second_last_name", e.target.value)}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Fecha de nacimiento" forceLabel>
            <Input
              type="date"
              value={form.data.birth_date}
              onChange={(e) => form.setData("birth_date", e.target.value)}
            />
          </Field>
          {isLosTilos && (
            <Field label="Cursos para">
              <SearchableSelect
                value={form.data.user_category}
                onChange={(value) => form.setData("user_category", value)}
                options={LOS_TILOS_CATEGORY_OPTIONS}
                required
              />
            </Field>
          )}
        </div>

        <SectionLabel>Datos de residencia</SectionLabel>

        <Field label="Dirección">
          <Input
            autoComplete="street-address"
            value={form.data.address}
            onChange={(e) => form.setData("address", e.target.value)}
            required
          />
        </Field>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Comuna">
            <Input
              value={form.data.commune}
              onChange={(e) => form.setData("commune", e.target.value)}
            />
          </Field>
          <Field label="Ciudad">
            <Input
              value={form.data.city}
              onChange={(e) => form.setData("city", e.target.value)}
            />
          </Field>
        </div>

        <SectionLabel>Actualizar contraseña</SectionLabel>

        <Field label="Nueva contraseña (opcional)">
          <PasswordInput
            autoComplete="new-password"
            minLength={6}
            value={form.data.password}
            onChange={(e) => form.setData("password", e.target.value)}
          />
        </Field>

        <Field label="Confirmar contraseña">
          <PasswordInput
            autoComplete="new-password"
            minLength={6}
            value={form.data.password_confirmation}
            onChange={(e) =>
              form.setData("password_confirmation", e.target.value)
            }
          />
        </Field>

        <div className="mt-2 flex flex-col items-center gap-3">
          <Button type="submit" disabled={form.processing} className="w-full">
            Actualizar datos
          </Button>
          <LinkButton href={profileUser(user.id).url}>Cancelar</LinkButton>
        </div>
      </form>
    </AuthCard>
  );
}

function EyeIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 12s3.75-7.5 9.75-7.5 9.75 7.5 9.75 7.5-3.75 7.5-9.75 7.5S2.25 12 2.25 12Z"
      />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      className="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.98 8.22A10.48 10.48 0 0 0 2.25 12s3.75 7.5 9.75 7.5c1.66 0 3.18-.4 4.52-1.05M6.6 6.6A9.9 9.9 0 0 1 12 4.5c6 0 9.75 7.5 9.75 7.5a13.2 13.2 0 0 1-2.16 3.02M3 3l18 18M9.9 9.9a3 3 0 0 0 4.2 4.2"
      />
    </svg>
  );
}
